import { addYears, format, isBefore, isEqual, subYears } from 'date-fns'
import { enGB, lt, lv, nl, pl } from 'date-fns/locale'

import { isEmpty, isNil } from '@/helpers/type-guards'
import i18n from '@/translations/i18n'

interface FormatDateTime {
  dateFormat?: string
}
export const DATE_FORMAT = 'yyyy-MM-dd'
export const DATE_FORMAT_WITH_MONTH_NAME = 'dd MMM yyyy'
export const DATE_FORMAT_WITH_TIME = 'dd/MM/yyyy HH:mm'
export const DATE_FORMAT_WITH_SLASH = 'dd/MM/yyyy'
export const DATE_FORMAT_WITH_MONTH_NAME_AND_TIME = 'dd MMM yyyy HH:mm'
export const DATE_DISPLAY_FORMAT = 'dd-MM-yyyy'
export const DATE_PLACEHOLDER = 'dd-mm-yyyy'
export const DATE_RANGE_PLACEHOLDER = 'dd-mm-yyyy / dd-mm-yyyy'
export const MONTH_DISPLAY_FORMAT = '"MM-yyyy"'
export const MONTH_PLACEHOLDER = 'mm-yyyy'
export const DATE_INPUT_FORMAT = '##-##-####'
export const DATE_RANGE_INPUT_FORMAT = '##-##-#### / ##-##-####'
export const MONTH_INPUT_FORMAT = '##-####'
export const DATE_INPUT_MASK = ['D', 'D', 'M', 'M', 'Y', 'Y', 'Y', 'Y']
export const DATE_RANGE_INPUT_MASK = ['D', 'D', 'M', 'M', 'Y', 'Y', 'Y', 'Y', 'D', 'D', 'M', 'M', 'Y', 'Y', 'Y', 'Y']
export const MONTH_INPUT_MASK = ['M', 'M', 'Y', 'Y', 'Y', 'Y']
export const MAX_DATE = new Date()
export const MIN_DATE = new Date('1900-01-01')

const isValidDate = (date: Date) => !isNaN(date.getTime())

export const getValidDate = (date: string | Date) => {
  if (!date) return null

  if (typeof date === 'string') {
    const first10Chars = date.slice(0, 10)

    // If the format has dots (e.g., "2023.05.23")
    if (first10Chars.includes('.')) {
      date = first10Chars.replace(/\./g, '-') // Replace dots with dashes
    }

    const parsedDate = new Date(date)
    return isValidDate(parsedDate) ? parsedDate : null
  }

  return isValidDate(date) ? date : null
}

const locales = { lv, lt, enGB, nl, pl }
export const formatDate = (date: Date | string, { dateFormat }: FormatDateTime = { dateFormat: '' }): string => {
  if (isNil(date) || isEmpty(date)) {
    return '-'
  }

  const validDate = getValidDate(date)
  if (!validDate) {
    return '-'
  }

  const locale = locales[i18n.language] || enGB
  return dateFormat ? format(validDate, dateFormat, { locale }) : format(validDate, DATE_FORMAT_WITH_MONTH_NAME, { locale })
}

export const convertDateToString = (date: Date) => {
  if (isNil(date) || isEmpty(date)) {
    return null
  }

  return format(getValidDate(date), DATE_FORMAT, { locale: locales[i18n.language] || enGB })
}

export const getMinDate = (minDate: string | Date) => getValidDate(minDate) || MIN_DATE
export const getMaxDate = (maxDate: string | Date) => getValidDate(maxDate) || MAX_DATE

export const aYearFromNow = new Date()
aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1)

export const getNYearFromNow = (years: number): string => {
  const currentDate = new Date()
  const futureDate = addYears(currentDate, years)
  return format(futureDate, 'yyyy-MM-dd')
}

export const getNYearTillNow = (years: number): string => {
  const currentDate = new Date()
  const pastDate = subYears(currentDate, years)
  return format(pastDate, 'yyyy-MM-dd')
}

export const areDatesEqual = (dateStr1: string | Date, dateStr2: string | Date) => {
  const utcDate1 = new Date(dateStr1)
  const utcDate2 = new Date(dateStr2)

  return isEqual(utcDate1, utcDate2)
}

export const isDateInPast = (inputDate: string) => {
  // Parse string into Date if needed
  const validDate = getValidDate(inputDate)

  // Compare with now
  return isBefore(validDate, new Date())
}
